import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Space, Table, Tag, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

import { useAccess } from '../../../auth/AccessContext.jsx';
import ActionIcon from '../../../components/ActionIcon.jsx';
import { useLiveTopic } from '../../../live/LiveContext.js';
import { formatInstant } from '../../../format';
import {
    CALENDAR_CODE, DAY_TYPES, DAY_TYPE_COLOUR, DAY_TYPE_LABEL, SOURCE_LABEL, changeLine, colomboMonth, dayTypeCounts, horizonNote, monthDays,
    monthLabel, monthQuery, shiftMonth
} from './calendar.js';

const { Text } = Typography;
const TABLE_DAY_TYPE_COLOUR = { ...DAY_TYPE_COLOUR, WEEKEND: 'gold' };

const columns = [
    {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        width: 150,
        render: (date, d) => <><Text>{date}</Text> <Text type="secondary">{d.weekday}</Text></>
    },
    {
        title: 'Date Type',
        dataIndex: 'dayType',
        key: 'dayType',
        width: 160,
        render: (dayType) => <Tag className="calendar-day-tag" color={TABLE_DAY_TYPE_COLOUR[dayType] ?? 'default'}>{DAY_TYPE_LABEL[dayType] ?? dayType}</Tag>
    },
    {
        title: 'Effective Day',
        dataIndex: 'isBusinessDay',
        key: 'isBusinessDay',
        width: 120,
        render: (v) => (v === null ? '—' : v ? 'YES' : 'NO')
    },
    {
        title: 'Holiday',
        key: 'holiday',
        render: (_, d) => (d.holidayName ? <>{d.holidayName}{d.isPublic && <Text type="secondary"> (public)</Text>}</> : '—')
    },
    {
        title: 'Source',
        key: 'source',
        className: 'calendar-source-cell',
        width: 170,
        render: (_, d) => (d.loaded
            ? <>{SOURCE_LABEL[d.source] ?? d.source ?? '—'}{d.sourceRef && <div><Text type="secondary" style={{ fontSize: 11.5 }}>{d.sourceRef}</Text></div>}</>
            : <Text type="secondary">{d.beyondHorizon ? 'Beyond the loaded calendar' : 'Not in the calendar'}</Text>)
    },
    {
        title: 'Changes',
        key: 'changes',
        render: (_, d) => (d.changes.length
            ? d.changes.map((c, i) => <div key={i}><Text type="secondary" style={{ fontSize: 11.5 }}>{changeLine(c, formatInstant)}</Text></div>)
            : '—')
    }
];

/**
 * One month of the calendar as calendar.js monthDays builds it from GET
 * settings/calendar: every date, as the Core holds it or "Not loaded", with
 * the holidays' names and what accepted CALENDAR files changed. Rendered by
 * CalendarPanel, and on its own by CalendarPanel.test.js.
 */
export const CalendarMonth = ({ days, loading = false, toolbar = null }) => {
    const counts = dayTypeCounts(days);
    return (
        <>
            <div className="calendar-table-toolbar">
                {toolbar}
                <Space wrap className="calendar-legend">
                    {[...DAY_TYPES, 'NOT_LOADED'].map((type) => (
                        <Tag key={type}>{DAY_TYPE_LABEL[type] + ': ' + (counts[type] ?? 0)}</Tag>
                    ))}
                </Space>
            </div>
            <Table
                className="calendar-table"
                rowKey="key"
                columns={columns}
                dataSource={days}
                loading={loading}
                pagination={false}
                size="small"
                rowClassName={(d) => (d.loaded ? '' : 'calendar-not-loaded')}
            />
        </>
    );
};

/**
 * Settings > Calendar (IP-104, IP-112): the dealing calendar the Core holds,
 * a month at a time (GET settings/calendar, today's month in Colombo first).
 * The calendar changes only through an accepted CALENDAR file on File
 * Uploads; this screen reads it.
 */
const CalendarPanel = () => {
    const { api } = useAccess();
    const [month, setMonth] = useState(() => colomboMonth());
    const [answer, setAnswer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshedAt, setRefreshedAt] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setAnswer(await api.settings.calendar(monthQuery(month)));
            setRefreshedAt(new Date());
        } catch (e) {
            setAnswer(null);
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [api, month]);
    // Live screens (docs/live-sync/implementation-plan.md 4.6): re-read, exactly as Refresh does, when
    // a calendar file was accepted.
    useLiveTopic(['calendar'], load);

    useEffect(() => {
        load();
    }, [load]);

    const thisMonth = colomboMonth();
    const days = answer ? monthDays(month, answer) : [];
    return (
        <div className="panel calendar-panel">
            <div className="calendar-heading">
                <h3 className="panel-title">Calendar</h3>
                <div className="permission-info-banner calendar-info">
                    <ActionIcon name="info" />
                    <span>
                        {answer?.calendarCode ?? CALENDAR_CODE}: the dealing calendar as the Core holds it, one row per date, weekends and
                        holidays included. {answer ? horizonNote(answer.horizon) : ''} Last refreshed{' '}
                        {refreshedAt ? refreshedAt.toLocaleTimeString('en-GB', { timeZone: 'Asia/Colombo' }) : '—'}.
                    </span>
                </div>
            </div>
            {error && <Alert type="error" showIcon message={error} className="auth-alert" closable onClose={() => setError(null)} />}
            {answer?.changesTruncated && (
                <Alert type="info" showIcon className="auth-alert" message="Only the first changes in this month are shown." />
            )}
            <CalendarMonth
                days={days}
                loading={loading}
                toolbar={(
                    <>
                        <Space className="calendar-month-controls" size={0}>
                            <Button icon={<LeftOutlined />} aria-label="Previous month" onClick={() => setMonth((m) => shiftMonth(m, -1))} />
                            <Text strong className="calendar-month">{monthLabel(month)}</Text>
                            <Button icon={<RightOutlined />} aria-label="Next month" onClick={() => setMonth((m) => shiftMonth(m, 1))} />
                            {month !== thisMonth && <Button className="calendar-this-month" onClick={() => setMonth(thisMonth)}>This month</Button>}
                        </Space>
                        <Button className="refresh-button calendar-refresh" icon={<ActionIcon name="refresh" />} onClick={load} loading={loading}>Refresh</Button>
                    </>
                )}
            />
        </div>
    );
};

export default CalendarPanel;
